/**
 * «Cancelar consulta» (D67 de `conversaciones-agente-ia`): quien arrobó al asistente —o le respondió,
 * que también lo arroba (D61)— puede cancelar el pedido desde su nota mientras el asistente no
 * respondió. La pantalla lo sabe por la actividad (D56): la nota tiene un «está pensando…» debajo.
 *
 * Arrobarlo de nuevo mientras responde no se impide: la consulta queda en la fila (D27).
 */
import type { NoteView } from "@/services/conversations.api";
import type { ActividadDelAsistente } from "./agent-activity";

/**
 * Se ofrece en la nota propia que el asistente tiene pendiente o en curso. Quién puede lo decide la
 * API: el autor de la nota.
 */
export function puedeCancelarConsulta(
  note: NoteView,
  me: number | null,
  actividad: ActividadDelAsistente | undefined,
): boolean {
  return (
    me != null &&
    note.authorUserId === me &&
    actividad?.eventType === "note.mention" &&
    actividad.noteId === note.noteId
  );
}

/**
 * Lo que queda de la actividad al cancelar la consulta de una nota, sin esperar el socket: deja de
 * verse «está pensando…». Si no había nada de esa nota, la misma lista.
 */
export function sinConsulta(
  items: ActividadDelAsistente[],
  noteId: string,
): ActividadDelAsistente[] {
  const quedan = items.filter(
    (a) => !(a.eventType === "note.mention" && a.noteId === noteId),
  );
  return quedan.length === items.length ? items : quedan;
}

/**
 * Lo que se le dice al operador cuando la API no deja cancelar. `releer`: lo que se ve está viejo
 * —el asistente ya respondió— y hay que volver a pedir la actividad. Un 403 o una consulta que ya no
 * está pendiente no son errores, son la regla.
 */
export function errorAlCancelarConsulta(e: unknown): {
  texto: string;
  nivel: "info" | "warning" | "error";
  releer: boolean;
} {
  const r = (
    e as {
      response?: {
        status?: number;
        data?: { code?: string; message?: unknown };
      };
    }
  )?.response;
  const code = r?.data?.code;
  if (code === "AGENT_REQUEST_CANCEL_DENIED" || (!code && r?.status === 403))
    return {
      texto: "Sólo quien arrobó al asistente puede cancelar la consulta",
      nivel: "warning",
      releer: false,
    };
  if (code === "AGENT_REQUEST_NOT_PENDING" || (!code && r?.status === 409))
    return {
      texto: "La consulta ya no está pendiente: el asistente ya respondió",
      nivel: "info",
      releer: true,
    };
  const detalle =
    typeof r?.data?.message === "string"
      ? r.data.message
      : (e as { message?: string })?.message ?? String(e);
  return {
    texto: `No se pudo cancelar la consulta: ${detalle}`,
    nivel: "error",
    releer: false,
  };
}
